/**
 * /earthworks/ai-quotes/:id — review an AI quote draft (EW-3).
 *
 * The draft as the API computed it, shown so a person can check it before any
 * quote exists:
 *   1. Readings — every input, with WHERE its value came from (the AI, the
 *      office, or a stated default), the AI's own words and where it saw them,
 *      and any flag (could not read it, unit not stated, disagrees with the
 *      office). A value the AI could not read stays blank: type it in.
 *   2. Lines — the quantities the CODE computed, each priced from a rate on the
 *      card. The rate per line can be changed or the line left off.
 *   3. Create quote — a draft quote through the normal quote path. It is still
 *      a draft there; nothing is sent.
 * Every figure here is the API's. Saving an edit returns the recomputed draft.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Bot, Loader2 } from 'lucide-react';
import { AiCapPrompt } from '../../components/ai/AiCapPrompt';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { aiCapReached, useCreateQuoteFromEwAiRun, useEwAiRun, useRetryEwAiRun, useUpdateEwAiRun } from '../../hooks/useEwAiQuotes';
import { canAdminEw, ewRefusal, fmtMoney } from '../../lib/earthworks';
import { queryLoadFailure } from '../../lib/load-failure';
import type { AiBudget, EwAiField, EwAiLine, EwAiRun } from '../../types/ewAiQuote';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwBadge, EwEmpty, EwFormError, EwLoading, EwPanel, isNotFound } from './EwBits';
import { EW_AI_KIND_LABEL, ewAiStatusSkin } from './AiQuotesScreen';

const SOURCE_LABEL: Record<string, string> = { ai: 'AI', office: 'Office', default: 'Default' };
const SOURCE_TONE: Record<string, string> = {
  ai: 'border-fi-amber/60 bg-fi-amber/15 text-fi-amber',
  office: 'border-fieldiq-border bg-fieldiq-card text-fieldiq-text',
  default: 'border-fieldiq-border bg-transparent text-fieldiq-muted',
};
const FLAG_TEXT: Record<string, string> = {
  unreadable: 'The AI could not read this. Enter it.',
  missing: 'Needed. Enter it.',
  unit_unknown: 'The AI read a number but not its unit. Enter it with a unit.',
  conflict: 'The AI read a different value.',
};
const LINE_STATUS: Record<string, string> = {
  priced: 'Priced',
  no_rate: 'No rate chosen',
  needs_input: 'Waiting on a reading',
  not_needed: 'Not needed',
};

/** The office's own value for a field, as the input shows it ('' = none). */
function officeText(run: EwAiRun, key: string): string {
  const v = run.inputs?.[key];
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
}

function SourceTag({ source }: { source: string | null }) {
  if (!source) return <span className="text-xs text-fieldiq-muted">—</span>;
  return (
    <span data-testid={`source-${source}`} className={`inline-flex items-center gap-1 rounded-fi-pill border px-2 py-0.5 text-[0.65rem] font-black uppercase tracking-[0.06em] ${SOURCE_TONE[source] ?? ''}`}>
      {source === 'ai' && <Bot className="size-3" aria-hidden="true" />}
      {SOURCE_LABEL[source] ?? source}
    </span>
  );
}

function FieldRow({ f, run, edit, onEdit, disabled }: {
  f: EwAiField; run: EwAiRun; edit: string | undefined; onEdit: (v: string) => void; disabled: boolean;
}) {
  const value = edit ?? officeText(run, f.key);
  const placeholder = f.source && f.source !== 'office' && f.display ? f.display : f.unit ? `in ${f.unit}` : '';
  const control = f.key === 'construction_type' ? (
    <select className={EW_INPUT} value={value} disabled={disabled} onChange={(e) => onEdit(e.target.value)} aria-label={f.label}>
      <option value="">{f.source === 'ai' && f.display ? `AI: ${f.display}` : 'Not set'}</option>
      {(run.constructionTypes ?? []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
    </select>
  ) : f.key === 'basement' || f.key === 'stockpile_backfill' ? (
    <select className={EW_INPUT} value={value} disabled={disabled} onChange={(e) => onEdit(e.target.value)} aria-label={f.label}>
      <option value="">{f.source && f.source !== 'office' && f.display ? `${SOURCE_LABEL[f.source]}: ${f.display}` : 'Not set'}</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  ) : (
    <input className={EW_INPUT} value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => onEdit(e.target.value)} aria-label={f.label} />
  );
  return (
    <tr data-testid={`ew-ai-field-${f.key}`} className="border-t border-fieldiq-border align-top">
      <td className="px-3 py-3">
        <p className="font-bold text-white">{f.label}{f.required ? '' : <span className="ml-1 text-xs font-normal text-fieldiq-muted">(optional)</span>}</p>
        {f.flag && (
          <p data-testid={`flag-${f.key}`} className={`mt-1 flex items-start gap-1 text-xs ${f.flag === 'conflict' ? 'text-fieldiq-muted' : 'text-fi-amber'}`}>
            <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
            {f.note || FLAG_TEXT[f.flag]}
          </p>
        )}
      </td>
      <td className="px-3 py-3">
        <p className="font-mono text-sm text-white" data-testid={`value-${f.key}`}>{f.display ?? '—'}</p>
        <div className="mt-1"><SourceTag source={f.source} /></div>
      </td>
      <td className="px-3 py-3 text-xs text-fieldiq-text">
        {f.aiText ? (
          <>
            <p>“{f.aiText}”</p>
            {f.aiEvidence && <p className="mt-0.5 text-fieldiq-muted">{f.aiEvidence}</p>}
          </>
        ) : (
          <span className="text-fieldiq-muted">—</span>
        )}
      </td>
      <td className="w-56 px-3 py-3">{control}</td>
    </tr>
  );
}

function LineRow({ l, run, onRate, disabled }: { l: EwAiLine; run: EwAiRun; onRate: (role: string, v: string) => void; disabled: boolean }) {
  const opts = run.rateOptions?.find((r) => r.role === l.role)?.options ?? [];
  const chosen = run.rateRoles?.[l.role];
  const value = chosen === null ? 'none' : chosen ? chosen : 'default';
  const cur = run.computed?.currency ?? null;
  return (
    <tr data-testid={`ew-ai-line-${l.role}`} className={`border-t border-fieldiq-border align-top ${l.status === 'not_needed' ? 'opacity-60' : ''}`}>
      <td className="px-3 py-3">
        <p className="font-bold text-white">{l.label}</p>
        {l.basis && <p className="mt-0.5 text-xs text-fieldiq-muted">{l.basis}</p>}
        <p className="mt-0.5 text-xs text-fieldiq-muted">{LINE_STATUS[l.status] ?? l.status}</p>
      </td>
      <td className="w-64 px-3 py-3">
        <select className={EW_INPUT} value={value} disabled={disabled || l.status === 'not_needed'} onChange={(e) => onRate(l.role, e.target.value)} aria-label={`Rate for ${l.label}`}>
          <option value="default">{l.rateChosen || !l.rateLabel ? 'Pick for me' : `Picked: ${l.rateCode ?? ''} ${l.rateLabel}`}</option>
          {opts.map((o) => (
            <option key={o.id} value={o.id}>{o.code ? `${o.code} · ` : ''}{o.label} — {fmtMoney(o.rate, o.currency)}{o.unit ? ` / ${o.unit}` : ''}</option>
          ))}
          <option value="none">Leave this line off</option>
        </select>
      </td>
      <td className="px-3 py-3 text-right font-mono text-sm text-white">{l.quantity ?? '—'}{l.quantity !== null && l.unit ? ` ${l.unit}` : ''}</td>
      <td className="px-3 py-3 text-right font-mono text-sm text-white">{fmtMoney(l.rate, cur)}</td>
      <td className="px-3 py-3 text-right font-mono text-sm font-bold text-white">{fmtMoney(l.total, cur)}</td>
    </tr>
  );
}

export default function EwAiQuoteDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUse = canAdminEw(user?.role);
  const q = useEwAiRun(canUse ? id : undefined);
  const run = q.data ?? null;
  const update = useUpdateEwAiRun(id ?? '');
  const retry = useRetryEwAiRun(id ?? '');
  const toQuote = useCreateQuoteFromEwAiRun(id ?? '');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [cap, setCap] = useState<Partial<AiBudget> | null>(null);
  useEffect(() => setEdits({}), [run?.id]);

  const c = run?.computed ?? null;
  const dirty = useMemo(() => Object.keys(edits).some((k) => run && edits[k] !== officeText(run, k)), [edits, run]);
  const editable = !!run?.editable;
  const busy = update.isPending || retry.isPending || toQuote.isPending;

  const save = async () => {
    if (!run) return;
    setError(null);
    const inputs: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(edits)) if (v !== officeText(run, k)) inputs[k] = v.trim() === '' ? null : v.trim();
    try {
      await update.mutateAsync({ inputs });
      setEdits({});
    } catch (e) { setError(ewRefusal(e, 'The draft could not be saved.')); }
  };
  const setRate = async (role: string, v: string) => {
    setError(null);
    try { await update.mutateAsync({ rate_roles: { [role]: v === 'none' ? null : v } }); }
    catch (e) { setError(ewRefusal(e, 'The rate could not be changed.')); }
  };
  const doRetry = async () => {
    setError(null); setCap(null);
    try { await retry.mutateAsync(); }
    catch (e) { const b = aiCapReached(e); if (b) setCap(b); else setError(ewRefusal(e, 'Could not retry.')); }
  };
  const createQuote = async () => {
    setError(null);
    try {
      const out = await toQuote.mutateAsync();
      if (out?.quoteId) navigate(`/quotes/${out.quoteId}`);
    } catch (e) { setError(ewRefusal(e, 'The quote could not be created.')); }
  };

  let body: React.ReactNode;
  if (!canUse) body = <EwPanel eyebrow="AI quotes" title="Draft"><EwEmpty>Only admins and office staff open AI quotes.</EwEmpty></EwPanel>;
  else if (q.isLoading) body = <EwLoading label="Loading the draft" />;
  else if (isNotFound(q.error)) body = <EwPanel eyebrow="AI quotes" title="Draft"><EwEmpty>That draft no longer exists.</EwEmpty></EwPanel>;
  else if (queryLoadFailure(q) || !run) body = <EwPanel eyebrow="AI quotes" title="Draft"><EwEmpty>The draft could not be loaded. Try again.</EwEmpty></EwPanel>;
  else if (run.status === 'queued' || run.status === 'processing') {
    body = (
      <EwPanel eyebrow="AI quotes" title="The AI is reading">
        <div className="flex items-center gap-3 p-6 text-sm text-fieldiq-text" role="status">
          <Loader2 className="size-5 animate-spin text-fi-amber" aria-hidden="true" />
          Reading {run.sourceFilenames.join(', ') || 'the files'}. This usually takes under a minute. The page updates by itself.
        </div>
      </EwPanel>
    );
  } else {
    body = (
      <div className="space-y-4">
        {cap && <AiCapPrompt budget={cap} />}
        {run.status === 'failed' && (
          <div role="alert" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 p-4 text-sm text-fieldiq-text">
            <p className="font-bold text-white">The AI reading did not finish</p>
            <p className="mt-1">{run.error || 'Unknown error.'} You can still enter every value by hand below.</p>
            <button type="button" className={`${EW_SECONDARY} mt-3`} onClick={doRetry} disabled={busy}>Try the AI again</button>
          </div>
        )}
        {run.quoteId && (
          <div className="rounded-fi-control border border-fi-green/40 bg-fi-green/10 p-4 text-sm text-fieldiq-text">
            This draft is quote <Link className="font-bold text-white underline" to={`/quotes/${run.quoteId}`}>{run.quoteNumber ?? 'open'}</Link>. Edit the quote there.
          </div>
        )}
        {(c?.warnings ?? []).map((w) => (
          <p key={w} data-testid="ew-ai-warning" className="flex items-start gap-2 rounded-fi-control border border-fi-amber/40 bg-fi-amber/10 px-3 py-2 text-sm text-fieldiq-text">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-amber" aria-hidden="true" />{w}
          </p>
        ))}
        <EwPanel eyebrow="Step 1" title="Readings — check every AI value" testId="ew-ai-fields"
          actions={editable ? <button type="button" className={EW_PRIMARY} onClick={save} disabled={!dirty || busy}>{update.isPending ? 'Saving…' : 'Save and reprice'}</button> : undefined}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <tr><th className="px-3 py-2">Input</th><th className="px-3 py-2">Used</th><th className="px-3 py-2">What the AI saw</th><th className="px-3 py-2">Office value</th></tr>
              </thead>
              <tbody>
                {(c?.fields ?? []).map((f) => (
                  <FieldRow key={f.key} f={f} run={run} edit={edits[f.key]} disabled={!editable || busy}
                    onEdit={(v) => setEdits((p) => ({ ...p, [f.key]: v }))} />
                ))}
              </tbody>
            </table>
          </div>
          {!!run.ai?.observations?.length && (
            <div className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-text">
              <p className="mb-1 font-black uppercase tracking-[0.12em] text-fieldiq-muted">The AI also noted</p>
              <ul className="list-disc space-y-0.5 pl-4">{run.ai.observations.map((o) => <li key={o}>{o}</li>)}</ul>
            </div>
          )}
        </EwPanel>
        <EwPanel eyebrow="Step 2" title="Lines — computed by FieldIQ, priced from your rate card" testId="ew-ai-lines"
          footer={c?.ready ? undefined : `Waiting on: ${(c?.missing ?? []).map((k) => c?.fields.find((f) => f.key === k)?.label ?? k).join(', ') || '—'}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <tr><th className="px-3 py-2">Line</th><th className="px-3 py-2">Rate</th><th className="px-3 py-2 text-right">Quantity</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Total</th></tr>
              </thead>
              <tbody>
                {(c?.lines ?? []).map((l) => <LineRow key={l.role} l={l} run={run} onRate={setRate} disabled={!editable || busy} />)}
              </tbody>
              <tfoot>
                <tr className="border-t border-fieldiq-border">
                  <td colSpan={4} className="px-3 py-3 text-right text-xs font-black uppercase tracking-[0.12em] text-fieldiq-muted">Subtotal, before tax</td>
                  <td data-testid="ew-ai-subtotal" className="px-3 py-3 text-right font-mono text-base font-extrabold text-white">{fmtMoney(c?.subtotal ?? null, c?.currency ?? null)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {!!c?.assumptions?.length && (
            <div className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-text">
              <p className="mb-1 font-black uppercase tracking-[0.12em] text-fieldiq-muted">Assumptions</p>
              <ul className="list-disc space-y-0.5 pl-4">{c.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
            </div>
          )}
        </EwPanel>
        {!!run.pages?.length && (
          <EwPanel eyebrow="Source" title={run.kind === 'photo_demo' ? 'Photos' : 'Plan pages'}>
            <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4">
              {run.pages.map((p, i) => (
                <a key={p.url} href={p.url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-fi-control border border-fieldiq-border">
                  <img src={p.url} alt={`Page ${i + 1}`} className="h-40 w-full object-cover" />
                </a>
              ))}
            </div>
          </EwPanel>
        )}
        <EwFormError message={error} />
        {editable && (
          <div className="flex flex-wrap items-center justify-end gap-3">
            {dirty && <span className="text-xs text-fi-amber">Save your changes first.</span>}
            <button type="button" className={EW_PRIMARY} onClick={createQuote} disabled={busy || dirty || !c?.ready || c?.subtotal === null}>
              {toQuote.isPending ? 'Creating…' : 'Create draft quote'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="ew-ai-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={run ? `AI quote · ${EW_AI_KIND_LABEL[run.kind] ?? run.kind}` : 'AI quote'}
        title={run?.title || 'AI quote draft'}
        description={run ? `${run.customerName ?? '—'}${run.siteName ? ` · ${run.siteName}` : ''}` : undefined}
        onBack={() => navigate('/earthworks/ai-quotes')}
        backLabel="Back to AI quotes"
        stats={run ? [
          { icon: <Bot className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', value: <EwBadge skin={ewAiStatusSkin(run.status)} value={run.status} /> },
          { icon: <Bot className="size-4" aria-hidden="true" />, label: 'Subtotal', tone: 'accent', value: fmtMoney(c?.subtotal ?? null, c?.currency ?? null) },
        ] : undefined}
        actions={run?.quoteId ? <Link className={DH_BAND_SECONDARY} to={`/quotes/${run.quoteId}`}>Open quote</Link> : undefined}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">{body}</div>
      </section>
    </div>
  );
}
